/**
 * School filtering utilities for level and exam pages.
 *
 * What: Pure filter functions that select schools matching a given page context.
 * Why: Level and exam pages each need a different subset of schools in their directory.
 * When: Update when new stream types or exam categories are added.
 */
import type { SchoolRecord } from '@shared/schools/schoolData.js';
import { isIbSchool } from '@shared/schools/schoolVariants.js';

/**
 * Filter secondary schools for a specific stream page.
 *
 * G1 / G2  → all non-IP secondary schools (offer the G1 and G2 tracks)
 * G3       → all secondary schools (G3 track + IP, which informally includes G3-equivalent)
 * IP       → IP schools only
 */
export function filterSecondaryByStream(
  schools: readonly SchoolRecord[],
  stream: 'g1' | 'g2' | 'g3' | 'ip'
): readonly SchoolRecord[] {
  switch (stream) {
    case 'g1':
    case 'g2':
      return schools.filter(s => s.servesSecondary && !s.ipInd);
    case 'g3':
      return schools.filter(s => s.servesSecondary);
    case 'ip':
      return schools.filter(s => s.servesSecondary && s.ipInd);
  }
}

/**
 * Filter secondary schools for exam / subject pages.
 *
 * o-level   → G3 + IP (all secondary)
 * n-level   → G1 + G2 (non-IP secondary only)
 * a-math    → G2 + G3 + IP (all secondary)
 * e-math    → G2 + G3 + IP (all secondary)
 * ip        → IP schools only
 * ib        → IB schools only (local IB schools + international schools)
 */
export function filterSecondaryByExam(
  schools: readonly SchoolRecord[],
  exam: 'o-level' | 'n-level' | 'a-math' | 'e-math' | 'ip' | 'ib'
): readonly SchoolRecord[] {
  switch (exam) {
    case 'o-level':
    case 'a-math':
    case 'e-math':
      return schools.filter(s => s.servesSecondary);
    case 'n-level':
      return schools.filter(s => s.servesSecondary && !s.ipInd);
    case 'ip':
      return schools.filter(s => s.servesSecondary && s.ipInd);
    case 'ib':
      return schools.filter(s => s.servesSecondary && isIbSchool(s));
  }
}
